import * as React from 'react';
import { useSelector, useDispatch } from "react-redux";
import '../styles.css';
//internal and external components import
import { Table, Tooltip, Typography } from '@mui/material';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import StatusIndicatorSmall from '../../StatusIndicatorSmall';
import ProgressBar from '../../ProgressBar';
import { sortData } from '../../../common/SortSupport';
import { EnhancedTableHead } from '../../EnhancedTableHead';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDownLeftAndUpRightToCenter, faLock, faLockOpen, faMaximize, faUnlock, faUpRightAndDownLeftFromCenter } from '@fortawesome/free-solid-svg-icons';
import { Box } from '@mui/material';
import { CustomTooltip } from '../../CustomTooltip';
import TableSkeleton from '../../TableSkeleton';
import { resetCriteriaActions, resetOffSiteStandards, resetSaStandards, resetSelectedSaCriteria, setCurrentEvaluationTab, setSelectedSaCriteria, updateCurrentSection, setSaCriteria } from '../../../redux/reducers/evaluation';
import { evaluationCriteriaActionsRegex, evaluationCriteriaEvidencesRegex, evaluationCriteriaRegex, evaluationCriterionRegex, evaluationSelectedCriteriaActionsRegex, evaluationSelectedStandardsActionsRegex, evaluationSelectedStandardsRegex, evaluationSelectedStandardsReportRegex, evaluationStandardsRegex, genUID, screenResolution } from '../../../common/StaticExports';
import CommentsDocsActionsIconWrapper from '../../CommentsDocsActionsIconWrapper';
import { useTranslation } from 'react-i18next';
import AccessManager from '../../../containers/AccessManager';
import { restrictCriteriaUsers } from '../../../api/evaluation';
import { setDrawer, setSnackBarData } from '../../../redux/reducers/common';
import { getAccessCount, hasUpdates } from '../../../common/logics';
import { useLocation, useNavigate } from 'react-router';


export default function CriteriaTable(props: any) {

  const { data, criteria, selectedCriteria, isLoading, triggerFetchCriteria } = props;
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const criteriaGroups = useSelector((state:any) => state.evaluation.criteriaGroups);
  const clubDetails = useSelector((state:any) => state.clubDetails);
  const [t] = useTranslation();
  const isAdmin = useSelector((state:any) => state.common.isAdmin);
  const currentCriterieGroup = criteriaGroups?.find((x:any) => x.id == selectedCriteria?.criteriaGroupId);
  const initialAccessState = {
    isOpen: false,
    itemId: null,
  }
  const [manageAccess, setManageAccess] = React.useState(initialAccessState);
  const userId = useSelector((state:any) => state.common.user)?.id;
  const criterias = useSelector((state: any) => state.evaluation.saCriteria);

  //supporting contents
  const [order, setOrder] = React.useState('asc');
  const [orderBy, setOrderBy] = React.useState('uid');

  const handleRequestSort = (
    event: React.MouseEvent<unknown>,
    property: any,
  ) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
    
  };

  const visibleRows:any = React.useMemo(
    () =>
      data?.length > 0 &&
      sortData(data, orderBy, order),
    [order, orderBy, data],
  );

  const head = isAdmin ? [
    {
      key: 'uid', displayName: 'id',
    },
    {
      key: 'name', displayName: 'criteria',
    },
    {
      key: '', displayName: '',
    },
    {
      key: 'offSite', displayName: 'off-site',
    },
    {
      key: 'onSite', displayName: 'on-site',
    },
    {
      key: 'finalReport', displayName: 'report',
    },
    {
      key: 'progress', displayName: 'club progress',
    },
    {
      key: 'offSiteProgress', displayName: 'off-site progress',
    },
    {
      key: 'reportProgress', displayName: 'report progress',
    },
  ] : [
    {
      key: 'uid', displayName: 'id',
    },
    {
      key: 'name', displayName: 'criteria',
    },
    {
      key: '', displayName: '',
    },
    {
      key: 'progress', displayName: 'club progress',
    },
    {
      key: '', displayName: '',
    },
  ]

  const manageTableExpand = () => {
    dispatch(resetSelectedSaCriteria());
    dispatch(resetSaStandards());
    navigate('/evaluation/criteria');
  }

  const manageTableShrink = async (d: any) => {
    dispatch(resetOffSiteStandards());
    dispatch(resetCriteriaActions());
    if (!data || data?.length == 0) {
      const criterias = await triggerFetchCriteria();
      const selectedCriteria = criterias?.find((item:any) => item?.id == d?.id);
      dispatch(setSelectedSaCriteria(selectedCriteria));
    } else {
      const selectedCriteria = data?.find((item:any) => item?.id == d?.id);
      dispatch(setSelectedSaCriteria(selectedCriteria));
    }
    if (!(evaluationCriterionRegex.test(location?.pathname) ||
        evaluationSelectedStandardsRegex.test(location?.pathname) ||
        evaluationCriteriaActionsRegex.test(location?.pathname) ||
        evaluationSelectedCriteriaActionsRegex.test(location?.pathname) ||
        evaluationCriteriaEvidencesRegex.test(location?.pathname) ||
        evaluationSelectedStandardsActionsRegex.test(location?.pathname) ||
        evaluationSelectedStandardsReportRegex.test(location?.pathname))) {
          navigate(`/evaluation/criteria/${d?.id}/standards`);
        }
    }

  React.useEffect(() => {
    if (!selectedCriteria && 
       (evaluationCriterionRegex.test(location?.pathname) ||
        evaluationStandardsRegex.test(location.pathname) ||
        evaluationSelectedStandardsRegex.test(location.pathname) ||
        evaluationSelectedStandardsReportRegex.test(location.pathname) ||
        evaluationCriteriaActionsRegex.test(location.pathname) ||
        evaluationSelectedCriteriaActionsRegex.test(location.pathname) ||
        evaluationCriteriaEvidencesRegex.test(location.pathname) ||
        evaluationSelectedStandardsActionsRegex.test(location.pathname))) {
        const tempsplitArr = location?.pathname?.split('/');
        manageTableShrink({ id: tempsplitArr[3]});
    }
  }, [navigate, location]);

  // React.useEffect(() => {
  //   if (data && data.length > 0 && criterias && criterias.length > 0) {
  //     const tempCurrentCriteriaIds = data?.map((c:any) => c.id);
  //     const tempFilteredCriterias = criterias?.filter((c:any) => tempCurrentCriteriaIds?.includes(c.id));
  //     hasUpdates(tempFilteredCriterias, data) && dispatch(setSaCriteria(tempFilteredCriterias));
  //   }
  // }, [criterias]);

  const manageUserAccess = (isUpdated: any, restrictedUsers:any) => {
    if(isUpdated) {
      var updatedCriteria = data?.find((x:any) => x.id == manageAccess?.itemId);
      updatedCriteria = {...updatedCriteria, restrictions: updatedCriteria?.restrictions?.map((res:any) => {
        if(restrictedUsers?.includes(res.userId)) {
          return {...res, isRestricted: true}
        } return {...res, isRestricted: false};
      })};
      const tempfilteredCriteria = data?.map((c:any) => c.id == updatedCriteria.id ? updatedCriteria : c);
      dispatch(setSaCriteria(tempfilteredCriteria));
      restrictCriteriaUsers({clubId: clubDetails?.id, criteriaId: manageAccess?.itemId, users: restrictedUsers})
      .then(() => {
        const warningData: any = {
          isOpen: true,
          severity: "success",
          message: t("ACCESS_UPDATED_SUCCESSFULLY"),
        };
        dispatch(setSnackBarData(warningData));
        triggerFetchCriteria();
      })
      .catch((error:any) => {
        const warningData: any = {
          isOpen: true,
          severity: "error",
          message: error?.message || t("ACCESS_UPDATE_FAILED"),
        };
        dispatch(setSnackBarData(warningData));
        dispatch(setSaCriteria(data));
      });
    }
    setManageAccess(initialAccessState);
  }
  
  //table contents
  return (
    <React.Fragment>
      <Box sx={{ height: '100%', position: 'relative' }}>
        {!criteria && <FontAwesomeIcon className='icon tableExpandIcon' icon={faDownLeftAndUpRightToCenter} onClick={() => manageTableExpand()} />}
        <AccessManager isOpen={manageAccess?.isOpen} handleIsOpen={manageUserAccess} from={'evaluationCriteria'} itemId={manageAccess?.itemId} />
        <TableContainer sx={{height: '100%', width: !criteria ? 'fit-content' : 'auto', overflow: criteria ? 'auto' : 'hidden'}}>
          <Table stickyHeader className={criteria ? 'criteriaTableWidth' : 'criteriaTableWidthAuto'} size="small" aria-label="a dense table">
            <EnhancedTableHead
              order={order}
              orderBy={orderBy}
              onRequestSort={handleRequestSort}
              bg={'#fff'}
              tableHeads={criteria ? head : [head[0]]}
            />
            <TableBody>
              {
                (!isLoading && visibleRows?.length > 0) ?
                visibleRows.map((d: any, i: number) => {
                  const totalUsers = d?.restrictions?.length || 0;
                  const accessCount = getAccessCount(d?.restrictions);
                  const isPrivate = totalUsers == accessCount ? false : true;
                  const restrictedUsers = d?.restrictions?.filter((res:any) => res.isRestricted)?.map((x:any) => x.userId) || [];
                  const isRestricted = restrictedUsers?.includes(userId);
                  return (
                  <TableRow
                    className={isRestricted ? 'selectedRow cursorNotAllowed' : 'hoverBG'}
                    key={`dynamic-table-row-${i}`}
                    onClick={() => !isRestricted && manageTableShrink(d)}
                  >
                    <CustomTooltip title={d.name} className={!criteria ? 'capitalize' : 'displayNone'} arrow followCursor placement='top-start'>
                      <TableCell 
                        className={(!criteria && selectedCriteria?.id === d?.id) ? 
                        'fw900 fs16 selectedRow selectedBlue' :
                         currentCriterieGroup?.id == d?.criteriaGroupId ? 'selectedRow' : ''}>
                          {genUID(d.uid)}
                      </TableCell>
                    </CustomTooltip>
                    {
                      criteria && <>
                      <TableCell 
                      className={isAdmin ? ' capitalize criteriaNameAdmin' : 'capitalize criteriaNameClub'}
                      sx={{cursor: isRestricted ? 'not-allowed' : 'pointer'}}>
                        {d.name}
                        {/* enable while implementing the new marker logic with eval report */}
                        {/* <NewMarker /> */}
                      </TableCell>
                      <TableCell className='fs12' sx={{minWidth: 170}}>
                        <CommentsDocsActionsIconWrapper 
                        actions={d.noOfActions} 
                        comments={isAdmin ? d.noOfComments : d.noOfClubComments} 
                        docs={d.noOfEvidences}
                        showDetailedCount={isAdmin}
                        jlComments={d.noOfJLComments}
                        clubComments={d.noOfClubComments} />
                      </TableCell>
                      {isAdmin && <TableCell><StatusIndicatorSmall reportType={'siteReview'} status={d.offSiteStatus} /></TableCell>}
                      {
                        isAdmin && 
                        <TableCell>
                          {
                            !(clubDetails.currentEvaluationPhase == "ON_SITE" || clubDetails.currentEvaluationPhase == "INTERIM_REPORT" ||
                              clubDetails.currentEvaluationPhase == "FINAL_REPORT") ? '-' :
                            <StatusIndicatorSmall reportType={'siteReview'} status={d.onSiteStatus} />
                          }
                        </TableCell>
                      }
                      {/* {
                        isAdmin &&
                        <TableCell>
                          {
                            !(clubDetails.currentEvaluationPhase == "INTERIM_REPORT" || clubDetails.currentEvaluationPhase == "FINAL_REPORT") ? '-' :
                            <StatusIndicatorSmall reportType={'siteReview'} status={d.interimReportStatus} />
                          }
                        </TableCell> 
                      } */}
                      {
                        isAdmin && 
                        <TableCell>
                          {
                            (clubDetails.currentEvaluationPhase == "FINAL_REPORT") ?
                            <StatusIndicatorSmall reportType={'siteReview'} status={d.finalReportStatus} /> : '-'
                          }
                        </TableCell>
                      }
                      <TableCell className={'criteriaProgress'}>
                        <ProgressBar percent={d.progress} />
                      </TableCell>
                      {
                        isAdmin &&
                        <TableCell className={'criteriaProgress'}>
                          <ProgressBar percent={d?.offSiteProgress ? d?.offSiteProgress : 0} />
                        </TableCell>
                      }
                      {
                        isAdmin &&
                        <TableCell className={'criteriaProgress'}>
                          <ProgressBar percent={d?.reportProgress ? d?.reportProgress : 0} />
                        </TableCell>
                      }
                      {
                        !isAdmin &&
                        <TableCell 
                        onClick={(e:any) => {
                          e.stopPropagation();
                          !isRestricted && setManageAccess({isOpen: true, itemId: d.id});
                        }}>
                          <Tooltip title={!isRestricted && t('TDM_MANAGE_ACCESS')} arrow placement="top"  className={isPrivate ? 'closedLock' : ''}>
                          <span>
                              <Typography className='fs12 lockCountLabel mr5'>{isPrivate ? accessCount : ''}</Typography>
                              <FontAwesomeIcon className={isPrivate ? 'icon closedLock' : 'icon'} icon={isPrivate ? faLock : faUnlock} onClick={(e:any) => {
                                  e.stopPropagation();
                                  !isRestricted && setManageAccess({isOpen: true, itemId: d.id});
                                  }} />
                          </span>
                          </Tooltip>
                        </TableCell>
                      }
                      </>
                    } 
                  </TableRow>
                )
                })
                :
                isLoading?
                <TableSkeleton rowCount={13} cellCount={head?.length} />:
                visibleRows?.length == 0 &&
                <TableRow>
                  <TableCell colSpan={head?.length} align={'center'} >
                    {t('NO_RECORDS_FOUND')}
                  </TableCell>
                </TableRow>
              }
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </React.Fragment>
  );
}